import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { normalize } from "../lib/textNormalize";
import { MapPin, X } from "lucide-react";

let CACHE = null;

export default function CityAutocomplete({ value, onChange, testId = "city-input", placeholder }) {
  const { t } = useI18n();
  const [cities, setCities] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const ref = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const load = async () => {
      if (CACHE) { setCities(CACHE); return; }
      try {
        const r = await api.get("/cities");
        CACHE = r.data.cities;
        setCities(r.data.cities);
      } catch (_e) { /* noop */ }
    };
    load();
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  // Match cities ignoring diacritics so "Siauliai" finds "Šiauliai".
  const normalizedQuery = normalize(query);
  const filtered = normalizedQuery
    ? cities.filter((c) => normalize(c).includes(normalizedQuery)).slice(0, 12)
    : cities.slice(0, 12);

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center bg-white border border-[#D1CFC7] rounded-md focus-within:ring-2 focus-within:ring-[#C85A32]">
        <MapPin className="w-4 h-4 ml-3 text-[#7A7A73]" />
        <input
          data-testid={testId}
          value={query}
          onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder || t("select_city")}
          className="w-full px-2 py-2.5 bg-transparent outline-none text-sm"
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(""); onChange(""); }}
            className="px-3 text-[#7A7A73] hover:text-[#1A1A18]"
            data-testid={`${testId}-clear`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-[#D1CFC7] rounded-md shadow-lg max-h-64 overflow-auto">
          {filtered.map((c) => (
            <button
              type="button"
              key={c}
              data-testid={`city-option-${c}`}
              onClick={() => { onChange(c); setQuery(c); setOpen(false); }}
              className="w-full text-left px-4 py-2 text-sm hover:bg-[#F0EFEB]"
            >
              {c}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
